import Rollbar from 'rollbar'
import debug from 'debug'

const debugLog = debug('dbcom')

// Generally we want to avoid vanilla console logs, but having them as part of the logger is ok
// because we can flag them on/off.
/* eslint-disable-next-line no-console */
debugLog.log = console.log.bind(console)

/**
 * Information logger for dbcom. This reports information to our logging systems as well as provides
 * developer tools for viewing log messages as part of the development and debugging process.
 * A new instance of the `Logger` should be created for every context. Settings are pulled from
 * the global `__USER__` and `__GLOBALS__` variables. Every instance of the `Logger` should include
 * a context name. This builds a scope so we can more easily identify the source of the log.
 *
 * @example
 * import { Logger } from '../dbcom'
 *
 * const logger = new Logger({ context: 'ComponentName' })
 */
class Logger {
  /**
   * Initializes the logger and the different logging services we use.
   *
   * @param {object} [options={}] - Setup options
   * @param {object} [payload={}] - Extra payload to log
   */
  constructor(options = {}) {
    const { config = {} } = window.__GLOBALS__ || {}
    const user = window.__USER__ || {}

    this.options = options

    this.rollbar = !!config.rollbarAccessToken && new Rollbar({
      enabled: !!(config.environment && ['stage', 'production'].includes(config.environment)),
      accessToken: config.rollbarAccessToken,
      environment: config.environment,
      reportLevel: 'warning',
      captureIp: 'anonymize',
      payload: {
        context: options.context,
        client: {
          javascript: {
            source_map_enabled: true,
          },
        },
        person: {
          id: user.id,
          email: user.email,
        },
      },
      ...options,
    })

    // Setup console logging scopes.
    this.print = {
      debug: debugLog.extend(['debug', options.context].filter(Boolean).join(':')),
      info: debugLog.extend(['info', options.context].filter(Boolean).join(':')),
      warning: debugLog.extend(['warning', options.context].filter(Boolean).join(':')),
      error: debugLog.extend(['error', options.context].filter(Boolean).join(':')),
      critical: debugLog.extend(['critical', options.context].filter(Boolean).join(':')),
    }

    this.critical = this.critical.bind(this)
    this.error = this.error.bind(this)
    this.warning = this.warning.bind(this)
    this.info = this.info.bind(this)
    this.debug = this.debug.bind(this)
  }

  /**
   * Logs a critical error. This level of error is for things that we never want to happen to our
   * users. This type of error should be logged if there's no chance the error was because of user
   * error and/or if the error broke the page or prevents the user from continuing on. This is the
   * kind of error we should be fixing immediately if it presents itself.
   *
   * @param {string} message - Error message to log
   * @param {object} [payload] - Any extra payload data to include
   */
  critical(message, payload) {
    const params = [message, payload].filter(Boolean)

    if (this.rollbar && typeof this.rollbar.critical === 'function') {
      this.rollbar.critical(...params)
    }

    this.print.critical(...params)
  }

  /**
   * Logs an error. This level of error is less severe than `critical`, but it still means something
   * went wrong. This type of error should be logged in a try/catch block and we aren't sure if the
   * error is our fault or if its just from the user entering bad data.
   *
   * @param {string} message - Error message to log
   * @param {object} [payload] - Any extra payload data to include
   */
  error(message, payload) {
    const params = [message, payload].filter(Boolean)

    if (this.rollbar && typeof this.rollbar.error === 'function') {
      this.rollbar.error(...params)
    }

    this.print.error(...params)
  }

  /**
   * Logs a warning. This log level is for things that we should probably look into, but that might
   * not explicitly affect the user. This is the kind of thing that our UI has a good fallback for
   * to handle, so the user either doesn't know or can continue on their way because of it.
   *
   * @param {string} message - Warning message to log
   * @param {object} [payload] - Any extra payload data to include
   */
  warning(message, payload) {
    const params = [message, payload].filter(Boolean)

    if (this.rollbar && typeof this.rollbar.warning === 'function') {
      this.rollbar.warning(...params)
    }

    this.print.warning(...params)
  }

  /**
   * Logs some information. This log level is used for things that are purely informational. Nothing
   * went wrong, but just some information we might find useful to have. Perhaps if we want to log
   * any time a user interacts with some complex/obscure feature, or any time a user meets some
   * edge case that we want to know about.
   *
   * @param {string} message - Info message to log
   * @param {object} [payload] - Any extra payload data to include
   */
  info(message, payload) {
    const params = [message, payload].filter(Boolean)

    if (this.rollbar && typeof this.rollbar.info === 'function') {
      this.rollbar.info(...params)
    }

    this.print.info(...params)
  }

  /**
   * Logs a debug message. This only formats and prints the message to the console if debugging is
   * turned on. This log type can be used whenever you want and as much as you want. It is most
   * helpful as part of method calls, or anywhere in the lifecycle of a function or component that
   * you feel might be useful to debug. These can be left in post-development to allow us to debug
   * specific features in production.
   *
   * @param {string} message - Debug message to log
   * @param {object} [payload] - Any extra payload data to include
   */
  debug(message, payload) {
    const params = [message, payload].filter(Boolean)

    this.print.debug(...params)
  }
}

export default Logger
